import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { List, X } from "@phosphor-icons/react";

const NAV_LINKS = [
  { to: "/", label: "Home", testid: "nav-home", end: true },
  { to: "/tours", label: "Tours", testid: "nav-tours" },
  { to: "/dates", label: "Book", testid: "nav-dates" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/partners", label: "Partner", testid: "nav-partners" },
];

export default function PublicNav({ overHero = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close drawer on route change
  useEffect(() => { setOpen(false); }, [pathname]);

  // lock body scroll while drawer open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const onDarkHero = overHero && !scrolled;
  const linkColor = onDarkHero ? "text-ms-cream/90 hover:text-white" : "text-ms-ink/70 hover:text-ms-ink";
  const activeColor = onDarkHero ? "text-white" : "text-ms-ink";
  const navCls = ({ isActive }) =>
    `text-xs tracking-[0.28em] uppercase transition-colors ${isActive ? activeColor : linkColor}`;
  const burgerColor = onDarkHero ? "text-white" : "text-ms-ink";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${onDarkHero ? "glass-nav-on-hero" : "glass-nav"}`}
        data-testid="public-nav"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className={`serif text-2xl tracking-tight ${onDarkHero ? "text-white" : "text-ms-ink"}`} data-testid="brand-link">
            Mystery <span className={onDarkHero ? "italic text-ms-brass-hi" : "italic text-ms-brass"}>Served</span>
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-10">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={navCls} data-testid={l.testid}>
                {l.label}
              </NavLink>
            ))}
          </div>

          {/* Desktop reserve button */}
          <Link
            to="/tours"
            className={`hidden md:inline-flex text-xs tracking-[0.28em] uppercase px-5 py-2.5 transition-all rounded-full
              ${onDarkHero
                ? "border border-white/40 text-white hover:bg-white hover:text-ms-ink"
                : "bg-ms-ink text-ms-cream hover:bg-ms-brass"}`}
            data-testid="nav-book-cta"
          >
            Reserve
          </Link>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={`md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full ${burgerColor} hover:opacity-80`}
            aria-label="Open menu"
            data-testid="mobile-nav-toggle"
          >
            <List size={26} weight="regular" />
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div
        className={`md:hidden fixed inset-0 z-[60] transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        data-testid="mobile-nav-drawer"
        aria-hidden={!open}
      >
        {/* backdrop */}
        <div
          className="absolute inset-0 bg-ms-ink/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="mobile-nav-backdrop"
        />

        {/* panel */}
        <aside
          className={`absolute top-0 right-0 h-full w-[88%] max-w-[380px] bg-ms-cream shadow-2xl
            transform transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}
            flex flex-col`}
        >
          <div className="flex items-center justify-between px-6 py-5 border-b border-ms-edge">
            <Link to="/" className="serif text-2xl text-ms-ink" data-testid="mobile-nav-brand">
              Mystery <span className="italic text-ms-brass">Served</span>
            </Link>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-11 h-11 inline-flex items-center justify-center rounded-full text-ms-ink hover:bg-ms-ink/5"
              aria-label="Close menu"
              data-testid="mobile-nav-close"
            >
              <X size={22} />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-6 py-8">
            <ul className="space-y-1">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    end={l.end}
                    className={({ isActive }) =>
                      `block py-4 serif text-2xl border-b border-ms-edge/60 transition-colors ${isActive ? "text-ms-brass" : "text-ms-ink hover:text-ms-brass"}`
                    }
                    data-testid={`mobile-${l.testid}`}
                  >
                    {l.label}
                  </NavLink>
                </li>
              ))}
            </ul>

            <Link
              to="/tours"
              className="mt-10 w-full inline-flex items-center justify-center text-xs tracking-[0.28em] uppercase px-5 py-4 rounded-full bg-ms-ink text-ms-cream hover:bg-ms-brass transition-colors"
              data-testid="mobile-nav-book-cta"
            >
              Reserve
            </Link>

            <div className="mt-10 pt-6 border-t border-ms-edge/60">
              <Link
                to="/privacy"
                className="text-xs tracking-[0.2em] uppercase text-ms-stone hover:text-ms-brass"
                data-testid="mobile-nav-privacy"
              >
                Privacy Policy
              </Link>
            </div>
          </nav>
        </aside>
      </div>
    </>
  );
}
